import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTeams } from "../lib/teams";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import {
  Trophy,
  Calendar,
  MapPin,
  Clock,
  Target,
  Video,
  ExternalLink,
  Award,
  TrendingUp,
} from "lucide-react";

function StatBox({ value, label, color = "text-slate-700" }) {
  return (
    <div className="text-center">
      <div className={`font-heading font-bold text-3xl tabular-nums ${color}`}>
        {value}
      </div>
      <div className="label-eyebrow mt-0.5">{label}</div>
    </div>
  );
}

function ResultBadge({ us, them }) {
  if (us == null || them == null) return null;
  const won = us > them;
  const drew = us === them;
  const cls = won
    ? "bg-emerald-100 text-emerald-700"
    : drew
    ? "bg-amber-100 text-amber-700"
    : "bg-red-100 text-red-700";
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold tabular-nums ${cls}`}>
      {us}–{them}
    </span>
  );
}

export default function ParentDashboard() {
  const { activeTeam, season } = useTeams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeTeam) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/teams/${activeTeam.id}/parent-dashboard`, { params: { season } });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.detail || "Errore");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTeam, season]);

  if (!activeTeam) return <p className="text-slate-500">Seleziona una squadra.</p>;
  if (error) {
    return (
      <Card className="p-8 border-slate-200">
        <p className="text-slate-600">{error}</p>
      </Card>
    );
  }
  if (!data) {
    return <p className="text-slate-400">Caricamento...</p>;
  }

  const { player, totals, next_match, recent_matches, veo_videos } = data;
  const playerName = `${player.first_name || ""} ${player.last_name || ""}`.trim();

  return (
    <div className="space-y-8" data-testid="parent-dashboard">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-14 w-14 rounded-2xl bg-sky-500 text-white flex items-center justify-center font-heading font-bold text-xl shrink-0">
          {player.jersey_number ?? "—"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-eyebrow">Spogliatoio di</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-1">
            {playerName || "Giocatore"}
          </h1>
          <p className="text-slate-500 mt-1">
            {activeTeam.name} · {activeTeam.category}
            {player.role && ` · ${player.role}`}
          </p>
        </div>
      </div>

      {/* Next match */}
      {next_match ? (
        <Card className="p-6 border-emerald-200 bg-emerald-50/50" data-testid="parent-next-match">
          <div className="label-eyebrow text-emerald-700">Prossima partita</div>
          <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <h2 className="font-heading text-2xl font-semibold">{next_match.title}</h2>
              {next_match.opponent && (
                <div className="text-slate-600 mt-0.5">vs {next_match.opponent}</div>
              )}
              <div className="text-sm text-slate-600 mt-3 flex flex-wrap gap-x-5 gap-y-1">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={14} className="text-emerald-600" />
                  {next_match.match_date}
                </span>
                {next_match.meeting_time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock size={14} className="text-emerald-600" />
                    Ritrovo {next_match.meeting_time}
                  </span>
                )}
                {(next_match.field?.name || next_match.location) && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={14} className="text-emerald-600" />
                    {next_match.field?.name || next_match.location}
                    {next_match.field?.address && ` · ${next_match.field.address}`}
                  </span>
                )}
              </div>
              {next_match.notes && (
                <p className="text-sm text-slate-500 mt-3 italic">{next_match.notes}</p>
              )}
            </div>
            <div className="shrink-0 h-14 w-14 rounded-2xl bg-emerald-500 text-white flex items-center justify-center">
              <Target size={26} />
            </div>
          </div>
        </Card>
      ) : (
        <Card className="p-6 border-slate-200 border-dashed text-center">
          <Trophy size={28} className="mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">Nessuna partita in programma.</p>
        </Card>
      )}

      {/* Season totals */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-center gap-2 mb-4">
          <Award size={18} className="text-sky-500" />
          <div className="label-eyebrow">Stagione di {playerName.split(" ")[0]}</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
          <StatBox value={totals.matches} label="Partite" />
          <StatBox value={totals.goals} label="Goal" color="text-sky-600" />
          <StatBox value={totals.assists} label="Assist" color="text-emerald-600" />
          <StatBox value={totals.minutes} label="Minuti" />
          <StatBox
            value={`${totals.yellow}/${totals.red}`}
            label="Gialli/Rossi"
            color={totals.red > 0 ? "text-red-600" : "text-amber-600"}
          />
        </div>
      </Card>

      {/* Recent matches */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={18} className="text-violet-500" />
          <div className="label-eyebrow">Ultime partite</div>
        </div>
        <div className="space-y-2" data-testid="parent-recent-matches">
          {recent_matches.length === 0 && (
            <Card className="p-8 text-center text-slate-500 border-dashed">
              Nessuna partita giocata ancora.
            </Card>
          )}
          {recent_matches.map((m) => {
            const ps = m.player_stat || {};
            return (
              <Card key={m.id} className="p-4 border-slate-200" data-testid={`recent-match-${m.id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-xs text-slate-400 tabular-nums w-20">
                    {m.match_date}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {m.title}
                      {m.opponent && (
                        <span className="text-slate-500"> · vs {m.opponent}</span>
                      )}
                    </div>
                  </div>
                  <ResultBadge us={m.final_score_us} them={m.final_score_them} />
                  <div className="flex gap-3 text-xs">
                    {ps.goals > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-sky-600 font-bold">
                        ⚽ {ps.goals}
                      </span>
                    )}
                    {ps.assists > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-emerald-600 font-bold">
                        🅰️ {ps.assists}
                      </span>
                    )}
                    {ps.yellow > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-amber-600 font-bold">
                        🟨 {ps.yellow}
                      </span>
                    )}
                    {ps.red > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-red-600 font-bold">
                        🟥 {ps.red}
                      </span>
                    )}
                    {ps.minutes > 0 && (
                      <span className="text-slate-500">{ps.minutes}'</span>
                    )}
                  </div>
                  {m.veo_video_url && (
                    <a
                      href={m.veo_video_url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-semibold hover:bg-violet-100"
                      title="Apri video Veo"
                    >
                      <Video size={12} /> Veo
                    </a>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Latest Veo videos */}
      {veo_videos.length > 0 && (
        <div data-testid="parent-veo-videos">
          <div className="flex items-center gap-2 mb-3">
            <Video size={18} className="text-violet-500" />
            <div className="label-eyebrow">Ultimi video Veo</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {veo_videos.map((v) => (
              <a
                key={v.id}
                href={v.veo_video_url}
                target="_blank"
                rel="noreferrer"
                className="block group"
              >
                <Card className="p-5 border-slate-200 hover:border-violet-300 hover:shadow-md transition">
                  <div className="flex items-start justify-between gap-2">
                    <div className="h-11 w-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center">
                      <Video size={20} />
                    </div>
                    <ExternalLink size={14} className="text-slate-400 group-hover:text-violet-500" />
                  </div>
                  <div className="mt-4">
                    <div className="font-heading font-semibold truncate">{v.title}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {v.match_date}
                      {v.opponent && ` · vs ${v.opponent}`}
                    </div>
                    {v.veo_notes && (
                      <p className="text-xs text-slate-500 mt-2 line-clamp-2 italic">
                        {v.veo_notes}
                      </p>
                    )}
                  </div>
                </Card>
              </a>
            ))}
          </div>
        </div>
      )}

      <Card className="p-4 border-slate-200 bg-slate-50">
        <p className="text-xs text-slate-500 text-center">
          Vuoi vedere il calendario completo? Vai alla sezione{" "}
          <Link to="/app/calendario" className="text-sky-600 underline">
            Calendario
          </Link>
          .
        </p>
      </Card>
    </div>
  );
}
